import type {
  MonthlyReviewDecisions,
  NextFollowUpKind,
  SetNextFollowUp,
} from "@care-by-ella/contracts";

// TES-978 — "Nästa uppföljning" in the monthly review, as the form holds it
// before it is saved. Pure on purpose (type-only imports, no path aliases):
// the unit tests in `apps/admin/test` run it with plain `node --test`.

export interface NextFollowUpDraft {
  kind: NextFollowUpKind;
  // YYYY-MM-DD, as <input type="date"> gives it.
  on: string;
  note: string;
}

/**
 * The form's starting point: the decision as saved, or else the next monthly
 * check-in on the day the server suggests. A suggestion, not a decision:
 * nothing is stored until the doctor saves.
 */
export function nextFollowUpDraftFrom(
  decisions: Pick<
    MonthlyReviewDecisions,
    "nextFollowUp" | "suggestedFollowUpOn"
  >,
): NextFollowUpDraft {
  const d = decisions.nextFollowUp;
  return d
    ? { kind: d.kind, on: d.on, note: d.note ?? "" }
    : { kind: "monthly_checkin", on: decisions.suggestedFollowUpOn, note: "" };
}

/** Why the draft cannot be saved yet, in the doctor's words; null when it can. */
export function nextFollowUpDraftProblem(
  draft: NextFollowUpDraft,
): string | null {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(draft.on)) return "Välj ett datum.";
  if (draft.kind === "other" && !draft.note.trim())
    return "Beskriv hur uppföljningen ska ske.";
  return null;
}

/** The draft as the API takes it; an empty note is left out. */
export function nextFollowUpBody(draft: NextFollowUpDraft): SetNextFollowUp {
  const note = draft.note.trim();
  return { kind: draft.kind, on: draft.on, ...(note ? { note } : {}) };
}

/** The draft differs from what is saved (or nothing is saved yet). */
export function nextFollowUpDraftChanged(
  draft: NextFollowUpDraft,
  saved: MonthlyReviewDecisions["nextFollowUp"],
): boolean {
  if (!saved) return true;
  return (
    draft.kind !== saved.kind ||
    draft.on !== saved.on ||
    draft.note.trim() !== (saved.note ?? "")
  );
}
